import React, { useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    List,
    ListItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { jsonForm } from '@europrocurement/flexy-form/utils/types/formTypes';
import {
    AccordionJsonFormsFiltered,
    type DossierFormulaire,
    useDossierFormulaireContext,
    useCurrentDossierCtx,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

type JsonV1FieldDef = { label: string; isCheckbox: boolean };
type JsonV1Entry = { label: string; value: string; isCheckbox: boolean };

const JSONV1_FIELDS: Record<string, JsonV1FieldDef> = {
    dos_rbe: { label: 'Demande de RBE', isCheckbox: true },
    dos_rne: { label: 'Mise à jour RNE', isCheckbox: true },
    dos_rpi: { label: 'Demande de SIE', isCheckbox: true },
    dos_res: { label: 'Hors du ressort', isCheckbox: true },
    dos_immat: { label: 'Contient une immatriculation', isCheckbox: true },
    dos_modif: { label: 'Contient une modification', isCheckbox: true },
    dos_parution: { label: 'Contient une publication', isCheckbox: true },
    dos_radiation: { label: 'Contient une radiation', isCheckbox: true },
    dos_divers: { label: 'Contient une formalité diverse', isCheckbox: true },
    dos_paraphe: { label: 'Contient un Paraphe', isCheckbox: true },
    dos_actes: { label: 'Contient un dépôt des actes', isCheckbox: true },
    dos_dap_an: { label: 'Contient un dépôt des comptes annuels', isCheckbox: true },
    dos_cap: { label: 'Capital', isCheckbox: false },
    dos_cdm: { label: 'La société dépend du registre des métiers', isCheckbox: true },
    dos_greffe: { label: 'Greffe', isCheckbox: false },
    dos_nbjustif: { label: 'Nb justif', isCheckbox: false },
    dos_effectif: { label: 'Effectif Salarié', isCheckbox: false },
    dos_reg_fisc: { label: 'Régime fiscal', isCheckbox: false },
    dos_reg_fisc_tva: { label: 'TVA', isCheckbox: false },
    dos_date_act: { label: "Date de début d'activité", isCheckbox: false },
    dos_activite: { label: 'Activité', isCheckbox: false },
    dos_maintient_act: { label: "Maintien de l'activité", isCheckbox: true },
    dos_nom_formalite: { label: 'Nom de la formalité', isCheckbox: false },
    dos_nom_com_sec: { label: 'Nom commercial', isCheckbox: false },
    dos_adresse_sec: { label: 'Adresse', isCheckbox: false },
    dos_enseigne_sec: { label: 'Enseigne', isCheckbox: false },
    dos_activite_sec: { label: 'Activité (sec.)', isCheckbox: false },
    dos_date_act_sec: { label: "Date de début d'activité (sec.)", isCheckbox: false },
    dos_effectif_sec: { label: 'Effectif Salarié (sec.)', isCheckbox: false },
    dos_effectif_tot: { label: 'Effectif Salarié total', isCheckbox: false },
    dos_ville_siege: { label: 'Ville du siège', isCheckbox: false },
    dos_cp_soc: { label: 'Code postal (soc.)', isCheckbox: false },
    dos_ville_soc: { label: 'Ville (soc.)', isCheckbox: false },
    type_form: { label: 'Type de formulaire', isCheckbox: false },
    compteur: { label: 'Compteur', isCheckbox: false },
    liste_jal: { label: 'Liste des JAL', isCheckbox: false },
};

type JsonV1PatternDef = {
    pattern: RegExp;
    label: (match: RegExpMatchArray) => string;
    isCheckbox: boolean;
};

const JSONV1_PATTERNS: JsonV1PatternDef[] = [
    {
        pattern: /^dos_cp_soc(\d+)$/,
        label: (m) => `Code postal (soc. ${m[1]})`,
        isCheckbox: false,
    },
    {
        pattern: /^dos_insee_soc(\d+)$/,
        label: (m) => `Code INSEE (soc. ${m[1]})`,
        isCheckbox: false,
    },
    {
        pattern: /^dos_greffe_soc(\d+)$/,
        label: (m) => `Greffe (soc. ${m[1]})`,
        isCheckbox: false,
    },
    {
        pattern: /^dos_ville_soc(\d+)$/,
        label: (m) => `Ville (soc. ${m[1]})`,
        isCheckbox: false,
    },
    { pattern: /^jal(\d+)$/, label: (m) => `JAL ${m[1]}`, isCheckbox: false },
];

const resolvePattern = (key: string): JsonV1FieldDef | null => {
    const hit = JSONV1_PATTERNS.find(({ pattern }) => pattern.test(key));
    if (!hit) return null;
    const match = key.match(hit.pattern) as RegExpMatchArray;
    return { label: hit.label(match), isCheckbox: hit.isCheckbox };
};

const decodeHtmlEntities = (str: string): string => {
    const cleaned = str.replace(/\\(&#\d+;|&\w+;)/g, '$1');
    const doc = new DOMParser().parseFromString(cleaned, 'text/html');
    return doc.documentElement.textContent ?? cleaned;
};

const parseJsonV1 = (raw: unknown): JsonV1Entry[] => {
    if (!raw) return [];
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof parsed !== 'object' || parsed === null) return [];
        const data = parsed as Record<string, unknown>;
        const mappedKeys = new Set(Object.keys(JSONV1_FIELDS));
        const mapped = Object.entries(JSONV1_FIELDS)
            .filter(([key]) => data[key] !== undefined && data[key] !== null && data[key] !== '')
            .map(([key, def]) => ({
                label: def.label,
                isCheckbox: def.isCheckbox,
                value: decodeHtmlEntities(String(data[key] ?? '')),
            }));
        const unmapped = Object.entries(data)
            .filter(
                ([key, val]) =>
                    !mappedKeys.has(key) && val !== undefined && val !== null && val !== '',
            )
            .map(([key, val]) => {
                const patternDef = resolvePattern(key);
                return {
                    label: patternDef ? patternDef.label : key,
                    isCheckbox: patternDef ? patternDef.isCheckbox : false,
                    value: decodeHtmlEntities(String(val)),
                };
            });
        return [...mapped, ...unmapped];
    } catch {
        return [];
    }
};

const formatJsonV1Value = (value: string, isCheckbox: boolean): string => {
    if (!isCheckbox) return value;
    if (value !== 'true' && value !== 'false') return value;
    return value === 'true' ? 'Oui' : 'Non';
};

const getJsonV1Color = (value: string, isCheckbox: boolean): string => {
    if (!isCheckbox) return 'text.primary';
    if (value !== 'true' && value !== 'false') return 'text.primary';
    return value === 'true' ? 'success.main' : 'text.disabled';
};

const PREPARATION_STATUSES = [
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_TO_CHECK,
];

const createObjectFromForm = (formConfig: jsonForm): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    (formConfig?.pages || []).forEach((page) => {
        (page.sections || [])
            .filter((section) => 'defaultvalue' in section && section.name)
            .forEach((section) => {
                const path = section.name.split('.');
                let current = result;

                path.forEach((key, index) => {
                    if (index === path.length - 1) {
                        (current as Record<string, unknown>)[key] = section.defaultvalue;
                    } else {
                        if (!current[key]) {
                            current[key] = {};
                        }
                        current = current[key] as Record<string, unknown>;
                    }
                });
            });
    });

    return result;
};

const iconSx = {
    display: 'flex',
    alignItems: 'center',
    width: 'auto',
    fontSize: '10px',
    lineHeight: '0',
};

const FormulaireTab: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const {
        dataLoaded,
        formulaires,
        currentPrestationId,
        setCurrentPrestationId,
        getCurrentFormulaire,
        updateFormulaire,
        persistAllFormulaires,
        hasNonPersistedChanges,
    } = useDossierFormulaireContext();
    const { enqueueSnackbar } = useSnackbar();

    const formulaire = getCurrentFormulaire();
    const isEditable = PREPARATION_STATUSES.includes(dossier?.statutId as number);

    const fdosEntries = useMemo(
        (): JsonV1Entry[] => parseJsonV1((dossier as Record<string, unknown>)?.jsonv1),
        [dossier],
    );

    const defaultValues = useMemo(
        () =>
            formulaire?.formulaireJson
                ? (_.merge(
                      {},
                      createObjectFromForm(formulaire.formulaireJson as jsonForm),
                      formulaire.prestationJson,
                  ) as Record<string, unknown>)
                : {},
        [formulaire],
    );

    const handleChange = useCallback(
        (data: Record<string, unknown>) => {
            if (formulaire) {
                updateFormulaire({
                    ...formulaire,
                    prestationJson: _.merge({}, formulaire.prestationJson, data),
                });
            }
        },
        [formulaire, updateFormulaire],
    );

    const handleSave = useCallback(async () => {
        try {
            await persistAllFormulaires();
            enqueueSnackbar('Formulaire enregistré avec succès');
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Une erreur est survenue lors de l'enregistrement", {
                variant: 'error',
            });
        }
    }, [persistAllFormulaires, enqueueSnackbar]);

    if (!dossier?.id || !dataLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '2em' }}>
                <CircularProgress />
            </Box>
        );
    }

    const showJsonV1 = dossier?.numero?.startsWith('LDF') && fdosEntries.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            {formulaires.length > 1 && (
                <List
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '0 20px',
                        padding: 0,
                    }}
                >
                    {formulaires.map((form: DossierFormulaire) => {
                        const isCurrent = form.prestationId === currentPrestationId;
                        return (
                            <ListItem
                                key={form.prestationId}
                                sx={{ width: 'auto', padding: 0 }}
                            >
                                <Tooltip
                                    title={form.label}
                                    arrow
                                    placement="top"
                                >
                                    <Button
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0 5px',
                                            fontSize: '13px',
                                            color: isCurrent ? 'primary.main' : 'text.secondary',
                                            ':hover': { color: 'primary.main' },
                                        }}
                                        onClick={() => setCurrentPrestationId(form.prestationId)}
                                    >
                                        {form.complete && (
                                            <FaOptionIcon
                                                icon={['fasdl', 'check']}
                                                sx={iconSx}
                                            />
                                        )}
                                        {form.label.length > 30
                                            ? `${form.label.substring(0, 27)}[...]`
                                            : form.label}
                                    </Button>
                                </Tooltip>
                            </ListItem>
                        );
                    })}
                </List>
            )}

            <Box
                sx={
                    showJsonV1
                        ? {
                              display: 'grid',
                              gridTemplateColumns: '2fr 3fr',
                              gap: '24px',
                              alignItems: 'start',
                          }
                        : {}
                }
            >
                {showJsonV1 && (
                    <Paper
                        elevation={1}
                        sx={{
                            position: 'sticky',
                            top: 16,
                            maxHeight: 'calc(100vh - 220px)',
                            overflow: 'auto',
                            padding: '12px 16px',
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, marginBottom: '8px' }}
                        >
                            Données du dossier
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {fdosEntries.map(({ label, value, isCheckbox }) => (
                                    <TableRow
                                        key={label}
                                        sx={{ '&:last-child td': { border: 0 } }}
                                    >
                                        <TableCell
                                            sx={{
                                                width: '50%',
                                                color: 'text.secondary',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                                verticalAlign: 'top',
                                                paddingY: '3px',
                                                userSelect: 'text',
                                            }}
                                        >
                                            {label}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: '12px',
                                                wordBreak: 'break-word',
                                                paddingY: '3px',
                                                userSelect: 'text',
                                                color: getJsonV1Color(value, isCheckbox),
                                            }}
                                        >
                                            {formatJsonV1Value(value, isCheckbox)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {!!formulaire?.formulaireJson && (
                        <Box sx={isEditable ? {} : { pointerEvents: 'none', opacity: 0.85 }}>
                            <AccordionJsonFormsFiltered
                                json={formulaire.formulaireJson as jsonForm}
                                defaultValues={defaultValues}
                                formulaireId={formulaire.formulaireId as number}
                                formaliteId={formulaire.formaliteId}
                                visibility="BACK"
                                expanded
                                onChange={isEditable ? handleChange : () => {}}
                            />
                        </Box>
                    )}

                    {isEditable && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingBottom: '16px',
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={!hasNonPersistedChanges}
                            >
                                Enregistrer
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default FormulaireTab;
